import jsPDF from 'jspdf';
import { svg2pdf } from 'svg2pdf.js';
import { renderToString } from 'react-dom/server';
import i18next from 'i18next';
import { renderFrameSVG, courtDimensions } from './Functions_Board_Grapchics.tsx';
import type { Frame, Player, Line, Cone, TextBox, Shade } from './DialogPlayBuilder.tsx';
import { UISETTINGSINIT } from './BoardConstants.ts';

type JsPdf = any;

type TypeState = {
    uiSettings?: {
        themePDFColor?: string;
        use3DPlayersPDF?: boolean;
        courtLineColor?: string;
        courtPaintAreaColor?: string;
        courtPaintAreaUseWoodPattern?: boolean;
        courtMidRangeColor?: string;
        courtMidRangeUseWoodPattern?: boolean;
        courtOutOfBoundsColor?: string;
        courtOutOfBoundsUseWoodPattern?: boolean;
    };
};

type DrupalEntity = {
    attributes: {
        title?: string;
        field_play_description?: string;
    };
    drupal_internal__nid?: number;
};

const parseHexColor = (hex?: string) => {
    if (!hex) return { r: 0, g: 0, b: 0 };
    const clean = hex.replace('#', '').trim();
    if (!/^[0-9a-fA-F]{3,6}$/.test(clean)) return { r: 0, g: 0, b: 0 };
    const expanded = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
    const num = parseInt(expanded, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

const setPDFColor = (doc: JsPdf, color?: string) => {
    const { r, g, b } = parseHexColor(color);
    doc.setDrawColor(r, g, b);
};

const setPDFTextColor = (doc: JsPdf, color?: string) => {
    const { r, g, b } = parseHexColor(color);
    doc.setTextColor(r, g, b);
};

const getCourtColors = (state?: TypeState) => ({
    lineColor: state?.uiSettings?.courtLineColor ?? UISETTINGSINIT.courtLineColor,
    paintAreaColor: state?.uiSettings?.courtPaintAreaColor ?? UISETTINGSINIT.courtPaintAreaColor,
    paintAreaUseWoodPattern: state?.uiSettings?.courtPaintAreaUseWoodPattern ?? UISETTINGSINIT.courtPaintAreaUseWoodPattern,
    midRangeColor: state?.uiSettings?.courtMidRangeColor ?? UISETTINGSINIT.courtMidRangeColor,
    midRangeUseWoodPattern: state?.uiSettings?.courtMidRangeUseWoodPattern ?? UISETTINGSINIT.courtMidRangeUseWoodPattern,
    outOfBoundsColor: state?.uiSettings?.courtOutOfBoundsColor ?? UISETTINGSINIT.courtOutOfBoundsColor,
    outOfBoundsUseWoodPattern: state?.uiSettings?.courtOutOfBoundsUseWoodPattern ?? UISETTINGSINIT.courtOutOfBoundsUseWoodPattern,
});

let cachedFooterLogo: string | null | undefined;

const loadLogoOnce = async () => {
    if (cachedFooterLogo !== undefined) return cachedFooterLogo;
    try {
        const response = await fetch("/images/icbBlue.png");
        if (!response.ok) throw new Error("Logo not found");
        const blob = await response.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(String(reader.result));
            reader.onerror = () => reject(new Error("Failed to read logo"));
            reader.readAsDataURL(blob);
        });
        cachedFooterLogo = dataUrl;
        return cachedFooterLogo;
    } catch (_error) {
        cachedFooterLogo = null;
        return cachedFooterLogo;
    }
};

const addFooterToPDF = async (doc: JsPdf, state?: TypeState) => {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    let logoDataUrl: string | null = null;
    try {
        logoDataUrl = await loadLogoOnce();
    } catch (_error) {
        logoDataUrl = null;
    }

    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);

        // Top decorative line
        doc.setLineWidth(12);
        setPDFColor(doc, state?.uiSettings?.themePDFColor);
        doc.line(0, 0, pageWidth, 0);

        // Footer text and image
        doc.setFontSize(9);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(120, 120, 120);

        const footerText = "Generated by iCoachBasketball";
        const textWidth = doc.getTextWidth(footerText);

        const imageWidth = 18;
        const imageHeight = 18;
        const imageX = pageWidth / 2 + textWidth / 2 + 6;
        const imageY = pageHeight - 28;

        if (logoDataUrl) {
            try {
                await doc.addImage(logoDataUrl, "PNG", imageX, imageY, imageWidth, imageHeight);
            } catch (_error) {
                // Ignore image errors to keep PDF generation working.
            }
        }

        doc.text(footerText, pageWidth / 2, pageHeight - 16, { align: "center" });
        doc.setTextColor(0, 0, 0);
    }
};

const addLogoPlaceholder = (doc: JsPdf, boxX: number, boxY: number, boxW: number, boxH: number) => {
    doc.setLineWidth(0.6);
    doc.setLineDashPattern([4, 3], 0);
    doc.setDrawColor(160, 160, 160);
    doc.rect(boxX, boxY, boxW, boxH);
    doc.setLineDashPattern([], 0);
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    const text = 'Logo';
    const textWidth = doc.getTextWidth(text);
    doc.text(text, boxX + (boxW - textWidth) / 2, boxY + boxH / 2 + 3);
    doc.setTextColor(0, 0, 0);
};

async function addCommonPageHeader(
    doc: JsPdf,
    state: TypeState | undefined,
    title: string,
    isFirstPage: boolean = false,
    margin: number = 20,
    headerHeight: number = 50,
    subtitle?: string,
    note?: string,
) {
    const pageWidth = doc.internal.pageSize.width;

    // Línea decorativa superior
    doc.setLineWidth(15);
    setPDFColor(doc, state?.uiSettings?.themePDFColor);
    doc.line(0, 0, pageWidth, 0);

    // Título
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    setPDFTextColor(doc, state?.uiSettings?.themePDFColor); // Theme color for main title
    const titleY = isFirstPage && !subtitle ? headerHeight - 5 : headerHeight - 10;
    doc.text(title, margin, titleY);

    // Subtítulo (solo en la primera página si se proporciona)
    let subtitleHeight = 0;
    if (isFirstPage && subtitle) {
        doc.setTextColor(80, 80, 80); // Darker gray for subtitle/date following the pattern
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");

        // Calcular el ancho disponible para el subtítulo (evitando el logo)
        const logoWidth = (isFirstPage ? 50 : 35) * 2; // Estimación aproximada del ancho del logo
        const availableWidth = pageWidth - margin * 2 - logoWidth - 10; // 10pt de espacio entre texto y logo

        // Dividir el texto en líneas
        const subtitleLines = doc.splitTextToSize(subtitle, availableWidth);

        // Calcular la altura total del subtítulo
        subtitleHeight = subtitleLines.length * 12; // 12pt por línea

        // Dibujar el subtítulo línea por línea
        subtitleLines.forEach((line: string, index: number) => {
            doc.text(line, margin, headerHeight + 5 + (index * 12));
        });
    }

    // Nota (si se proporciona)
    let noteHeight = 0;
    if (note) {
        doc.setTextColor(80, 80, 80); // Darker gray for note matching date color
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        const noteText = note;

        // Reducir el ancho disponible para la nota dejando 60 puntos de margen a la derecha
        const noteLines = doc.splitTextToSize(noteText, pageWidth - 2 * margin - 60);
        const noteY = headerHeight + (isFirstPage && subtitle ? subtitleHeight + 8 : 8);
        noteLines.forEach((line: string, index: number) => {
            doc.text(line, margin, noteY + (index * 12));
        });
        noteHeight = noteLines.length * 12 + 5;
    }

    // Ajustar la posición de la línea divisoria basada en el contenido
    const dividerY = headerHeight + (isFirstPage && subtitle ? subtitleHeight + 15 : isFirstPage ? 25 : 10) + noteHeight;

    // Línea divisoria
    doc.setLineWidth(0.2);
    setPDFColor(doc, state?.uiSettings?.themePDFColor);
    doc.line(margin, dividerY, pageWidth - margin, dividerY);

    // Placeholder del logo del club
    const logoHeight = isFirstPage ? 50 : 35;
    const logoWidth = logoHeight * 2;
    const logoY = (dividerY - logoHeight) / 2 + 4;
    const logoX = pageWidth - margin - logoWidth;
    addLogoPlaceholder(doc, logoX, logoY, logoWidth, logoHeight);

    // Reset text color to default
    doc.setTextColor(0, 0, 0);

    return dividerY + 10; // Retorna la posición Y donde debe comenzar el contenido
}

export async function generatePDFPlay(state: TypeState | null, play: DrupalEntity, playDetails: {
    courtType: 'halfcourt' | 'fullcourt',
    frames: Array<{
        players: Player[],
        lines: Line[],
        cones: Cone[],
        texts?: TextBox[],
        shades?: Shade[],
        description?: string
    }>
}, returnBlob: boolean = false): Promise<Blob | void> {
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const frames = playDetails.frames;
    const safeState: TypeState = state ?? { uiSettings: { themePDFColor: '#00398F' } };

    // Configuration of colors and margins
    const margin = 20;
    // const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const footerHeight = 30;
    const maxContentHeight = pageHeight - margin - footerHeight;
    const headerHeight = 50;

    // A4 dimensions in points (pt)
    const A4_WIDTH = 595;
    const FRAMES_PER_ROW = 2;
    const FRAME_SPACING = 15; // Space between frames
    const FRAME_WIDTH = (A4_WIDTH - 2 * margin - FRAME_SPACING * (FRAMES_PER_ROW - 1)) / FRAMES_PER_ROW;
    const FRAME_HEIGHT = FRAME_WIDTH * (courtDimensions[playDetails.courtType].height / courtDimensions[playDetails.courtType].width);
    const DESCRIPTION_FONT_SIZE = 8;
    const DESCRIPTION_LEFT_MARGIN = playDetails.courtType === "halfcourt" ? 15 : 10;

    // Use common header for the first page
    let yPosition = await addCommonPageHeader(
        doc,
        safeState,
        play.attributes.title || "Basketball Play",
        true,
        margin,
        headerHeight,
        play.attributes.field_play_description || i18next.t("Play"),
        ''
    );

    // Ensure the divider line is below the logo/description
    yPosition = Math.max(yPosition, 100);

    // Add extra margin for frame content
    yPosition += 20;

    // Create temporary container for SVGs
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    document.body.appendChild(container);

    // Create SVGs for each frame
    for (let frameIndex = 0; frameIndex < frames.length; frameIndex++) {
        const frame = frames[frameIndex];
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('width', courtDimensions[playDetails.courtType].width.toString());
        svg.setAttribute('height', courtDimensions[playDetails.courtType].height.toString());
        svg.setAttribute('viewBox', `0 0 ${courtDimensions[playDetails.courtType].width} ${courtDimensions[playDetails.courtType].height}`);

        const renderedFrame = renderFrameSVG(
            {
                ...frame,
                id: frameIndex + 1
            },
            playDetails.courtType,
            play.drupal_internal__nid,
            {
                hideLogo: true,
                hideBorders: true,
                courtColors: getCourtColors(safeState),
                isForPDF: true,
                use3DPlayers: safeState.uiSettings?.use3DPlayersPDF ?? UISETTINGSINIT.use3DPlayers,
                allow3DForPDF: safeState.uiSettings?.use3DPlayersPDF ?? UISETTINGSINIT.use3DPlayers
            }
        );

        svg.innerHTML = renderToString(renderedFrame);
        container.appendChild(svg);
    }

    // Process frames in groups based on FRAMES_PER_ROW
    for (let index = 0; index < frames.length; index += FRAMES_PER_ROW) {
        const rowFrames = frames.slice(index, index + FRAMES_PER_ROW);
        let rowHeight = FRAME_HEIGHT; // Minimum row height

        // Check if there is enough space for the next row
        if (yPosition + FRAME_HEIGHT + 40 > maxContentHeight) {
            doc.addPage();
            yPosition = await addCommonPageHeader(doc, safeState, play.attributes.title || "Basketball Play", false, margin, headerHeight, '', '');
            // Add extra margin for frame content on new pages
            yPosition += 20;
        }

        // Draw frames in the current row
        for (let colIndex = 0; colIndex < rowFrames.length; colIndex++) {
            const x = margin + colIndex * (FRAME_WIDTH + FRAME_SPACING);

            // Frame border/box removed for plays

            // Frame title
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            setPDFTextColor(doc, safeState.uiSettings?.themePDFColor);
            doc.text(`${i18next.t("PlayCard00")} ${index + colIndex + 1}`, x + FRAME_WIDTH / 2, yPosition - 5, { align: "center" });

            // Draw the frame from the temporary container
            const frameElement = container.children[index + colIndex] as SVGSVGElement;
            if (!frameElement) continue;

            await svg2pdf(frameElement, doc, {
                x: x + 5,
                y: yPosition + 5,
                width: FRAME_WIDTH - 10,
                height: FRAME_HEIGHT,
            });

            // Frame description
            if (rowFrames[colIndex].description) {
                doc.setFontSize(DESCRIPTION_FONT_SIZE);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(60, 60, 60);
                const splitDescription = doc.splitTextToSize(
                    rowFrames[colIndex].description!,
                    FRAME_WIDTH - 10 - DESCRIPTION_LEFT_MARGIN
                );
                doc.text(
                    splitDescription,
                    x + DESCRIPTION_LEFT_MARGIN,
                    yPosition + FRAME_HEIGHT + 15
                );
                doc.setTextColor(0, 0, 0); // Reset color after description
                // Update maximum description height for the row
                const descriptionHeight = splitDescription.length * (DESCRIPTION_FONT_SIZE + 2);
                rowHeight = Math.max(rowHeight, FRAME_HEIGHT + descriptionHeight + 30);
            } else {
                rowHeight = Math.max(rowHeight, FRAME_HEIGHT + 20);
            }

            // Reset text color
            doc.setTextColor(0, 0, 0);
        }

        yPosition += rowHeight + 20;
        rowHeight = 0; // Reset for the next row
    }

    // Footer on all pages
    await addFooterToPDF(doc, safeState);

    // Clean up temporary container
    document.body.removeChild(container);

    // Return blob or save the PDF based on parameter
    if (returnBlob) {
        return doc.output('blob');
    } else {
        const fileName = play.attributes.title ? `${play.attributes.title}.pdf` : `${i18next.t("Functions_PDF02")} ${play.attributes.title}.pdf`;
        doc.save(fileName);
    }
}